import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AttachFilesSection } from '@/components/shared'
import type { AttachmentRef } from '@/components/shared'
import { contractsApi } from '@/services/contracts'
import { clientsApi } from '@/services/clients'
import { settingsApi } from '@/services/settings'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { DOCUMENT_FILE_ACCEPT } from '@/lib/constants'
import type { Documentable } from '@/types'

interface Props {
  contractId: string
  clientId?: string
  hideHeading?: boolean
  hideUploadLabel?: boolean
  brandTabs?: boolean
  uploadTabLabel?: string
  readOnly?: boolean
  documents?: Documentable[]
  onDocumentsChanged?: () => void
  openPreviewOnClick?: boolean
}

function attachmentSource(
  docType: string | null | undefined
): AttachmentRef['source'] {
  if (docType === 'client' || docType === 'library') return docType
  return 'upload'
}

// Contract attachments (feedback #59). Wraps the shared AttachFilesSection,
// wiring it to the contract document endpoints (upload new / pick from the
// client's documents / pick from the Settings document library). Persists each
// change immediately. Mirrors QuoteAttachments.
export function ContractAttachments({
  contractId,
  clientId,
  hideHeading,
  hideUploadLabel,
  brandTabs,
  uploadTabLabel,
  readOnly,
  documents,
  onDocumentsChanged,
  openPreviewOnClick,
}: Props) {
  const { t } = useTranslation('contracts')
  const [localValue, setLocalValue] = useState<{
    source: Documentable[] | undefined
    value: AttachmentRef[]
  } | null>(null)
  const value = useMemo(
    () =>
      localValue?.source === documents && localValue
        ? localValue.value
        : (documents?.map(toAttachmentRef) ?? []),
    [documents, localValue]
  )

  const setValue = useCallback(
    (
      next: AttachmentRef[] | ((current: AttachmentRef[]) => AttachmentRef[])
    ) => {
      setLocalValue(current => {
        const previous =
          current?.source === documents && current
            ? current.value
            : (documents?.map(toAttachmentRef) ?? [])
        return {
          source: documents,
          value: typeof next === 'function' ? next(previous) : next,
        }
      })
    },
    [documents]
  )

  // Load the currently-attached documents.
  useEffect(() => {
    if (documents) return
    let active = true
    contractsApi
      .documents(contractId, { limit: 100 })
      .then(res => {
        if (!active) return
        setValue((res.data ?? []).map(toAttachmentRef))
      })
      .catch(() => {
        /* leave empty on failure */
      })
    return () => {
      active = false
    }
  }, [contractId, documents, setValue])

  const uploadFn = useCallback(
    async (file: File): Promise<AttachmentRef> => {
      const res = await contractsApi.uploadDocument(contractId, file)
      const d = res.data!
      return {
        id: d.id,
        file_id: d.file_id,
        name: d.file_name ?? file.name,
        url: d.url,
        mime_type: d.file_type,
        size: d.file_size,
        created_at: d.created_at,
        source: 'upload',
      }
    },
    [contractId]
  )

  const listClientDocsFn = useCallback(
    async (cId: string): Promise<AttachmentRef[]> => {
      const res = await clientsApi.documents(cId, { limit: 100 })
      return (res.data ?? []).map(d => ({
        id: d.file_id,
        file_id: d.file_id,
        name: d.file_name,
        url: d.url,
        mime_type: d.file_type,
        size: d.file_size,
        created_at: d.created_at,
        source: 'client' as const,
      }))
    },
    []
  )

  const listLibraryFn = useCallback(async (): Promise<AttachmentRef[]> => {
    const res = await settingsApi.reusableDocumentOptions('contract')
    return (res.data ?? []).map(d => ({
      id: d.file_id,
      file_id: d.file_id,
      name: d.name,
      created_at: d.created_at,
      source: 'library' as const,
    }))
  }, [])

  // Persist add (link existing) / remove against the contract.
  const onChange = useCallback(
    async (next: AttachmentRef[]) => {
      const prev = value
      setValue(next)

      const added = next.filter(n => !prev.some(p => sameRef(p, n)))
      const removed = prev.filter(p => !next.some(n => sameRef(p, n)))

      try {
        for (const ref of added) {
          // Freshly-uploaded refs are already persisted by uploadFn; only
          // client/library picks need a link call.
          if (ref.source === 'upload') continue
          if (!ref.id) continue
          const res = await contractsApi.attachExistingDocument(
            contractId,
            ref.id,
            ref.source
          )
          setValue(curr =>
            curr.map(c => (sameRef(c, ref) ? { ...c, id: res.data?.id } : c))
          )
        }
        for (const ref of removed) {
          if (ref.id) await contractsApi.deleteDocument(contractId, ref.id)
        }
        onDocumentsChanged?.()
      } catch (e) {
        setValue(prev)
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(e, t('detail.attachments.empty')),
        })
      }
    },
    [contractId, onDocumentsChanged, setValue, value, t]
  )

  const onRename = useCallback(
    async (item: AttachmentRef, name: string) => {
      if (item.source !== 'upload' || !item.id) return
      const response = await contractsApi.renameDocument(
        contractId,
        item.id,
        name
      )
      toast({
        variant: 'success',
        title: t('detail.attachments.renameSuccess'),
      })
      onDocumentsChanged?.()
      return {
        name: response.data?.file_name ?? item.name,
        url: response.data?.url ?? item.url,
      }
    },
    [contractId, onDocumentsChanged, t]
  )

  return (
    <AttachFilesSection
      value={value}
      onChange={onChange}
      clientId={clientId}
      accept={DOCUMENT_FILE_ACCEPT}
      uploadFn={uploadFn}
      listClientDocsFn={clientId ? listClientDocsFn : undefined}
      listLibraryFn={listLibraryFn}
      libraryTarget='contract'
      preloadLibrary
      onRename={onRename}
      hideHeading={hideHeading}
      hideUploadLabel={hideUploadLabel}
      brandTabs={brandTabs}
      uploadTabLabel={uploadTabLabel}
      readOnly={readOnly}
      compact={readOnly}
      openPreviewOnClick={openPreviewOnClick}
      emptyMessage={documents ? t('detail.attachments.empty') : undefined}
    />
  )
}

function toAttachmentRef(d: Documentable): AttachmentRef {
  return {
    id: d.id,
    file_id: d.file_id,
    name: d.file_name ?? '',
    url: d.url,
    mime_type: d.file_type,
    size: d.file_size,
    created_at: d.created_at,
    source: attachmentSource(d.doc_type),
  }
}

// Identity across optimistic updates: match on id when present, else name+url.
function sameRef(a: AttachmentRef, b: AttachmentRef): boolean {
  if (a.file_id && b.file_id) return a.file_id === b.file_id
  if (a.id && b.id) return a.id === b.id
  return a.name === b.name && (a.url ?? '') === (b.url ?? '')
}

export default ContractAttachments
